/**
 * Pretty-prints a Handlebars email body by breaking it onto lines — the "prettifying pass" of
 * AU-69.
 *
 * **Why not Monaco's HTML formatter.** Monaco ships an HTML language service that covers the
 * `handlebars` language for colorization, folding and hovers, but it disables formatting there
 * on purpose — `documentFormattingEdits: languageId === htmlLanguageId`, commented in its own
 * source as "turned off for Razor and Handlebar". A general HTML formatter reflows text and
 * re-indents inside elements, which mangles `{{#if}}` / `{{#each}}` blocks that legally straddle
 * tags. Borrowing it would trade one-line bodies for corrupted ones.
 *
 * **The rule instead.** A line break is inserted between two adjacent tags or block directives
 * only when **at least one of them is a block-level tag**, and never anywhere else. That is
 * exactly the boundary at which HTML discards whitespace, so the reformatting cannot change how
 * an email renders.
 *
 * Requiring a block-level *tag* — rather than accepting any block directive — is load-bearing,
 * and an adversarial review is what established it. `{{#if A}}X{{/if}}{{#if B}}Y{{/if}}` inside a
 * `<p>` has two adjacent directives with inline content between them; breaking there turns `XY`
 * into `X Y`. Worse, breaking between `{{#each}}` and an inner `{{#if}}` puts the newline *inside*
 * the loop body, so every iteration gains one. Both are visible in a customer's inbox.
 *
 * Nothing is indented and no text is reflowed. Both would be cosmetic improvements that require
 * knowing which whitespace is significant, and in email HTML — where a stray space between two
 * inline elements is visible, and Outlook's Word engine has its own opinions — that knowledge is
 * not worth the risk.
 */

/**
 * Elements whose surrounding whitespace HTML discards. Inline elements (`a`, `span`, `strong`,
 * `img`, …) are deliberately absent: a break beside one renders as a space.
 *
 * `td` and `th` are absent too. Whitespace inside a `<tr>` is in fact dropped, but keeping cells
 * together holds the approval-button row on one line, which is the markup BUG-057 settled on to
 * survive Outlook's Word engine.
 */
const BLOCK_TAGS = new Set([
  'blockquote',
  'body',
  'center',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'hr',
  'html',
  'li',
  'ol',
  'p',
  // Block-level from the outside, raw text on the inside: breaks may sit beside a <pre>,
  // and RAW_TEXT_TAGS below stops any landing within it.
  'pre',
  'table',
  'tbody',
  'tfoot',
  'thead',
  'tr',
  'ul',
]);

/**
 * Elements whose content is raw text, where **every** character is significant — a newline inside
 * one renders literally. A customer pasting a monospaced usage table into an override is the
 * realistic case. While inside one of these, the formatter emits verbatim.
 */
const RAW_TEXT_TAGS = new Set(['pre', 'textarea', 'script', 'style']);

type TokenKind = 'tag' | 'block-directive' | 'expression' | 'comment' | 'text';

interface Token {
  kind: TokenKind;
  text: string;
  /** Lower-cased element name, for a `tag` token. */
  name?: string;
  /** Whether a `tag` token is a closing tag. */
  closing?: boolean;
}

/**
 * Order matters. Comments come first so a conditional comment (`<!--[if mso]> … <![endif]-->`)
 * is captured whole and never looked inside — TD-383's markup survives a format untouched.
 *
 * The tag branch is quote-aware: `<div title="a > b">` contains a `>` inside an attribute, and a
 * naive `[^>]*` truncates the token there, which let a following space be dropped and a newline
 * written into the middle of the attribute value.
 */
const TOKEN_PATTERN =
  /(<!--[\s\S]*?-->)|(<\/?[a-zA-Z][a-zA-Z0-9-]*(?:"[^"]*"|'[^']*'|[^>"'])*>)|(\{\{[^}]*\}\})/g;

function tokenize(source: string): Token[] {
  // A fresh regex per call: a module-level /g pattern carries `lastIndex` between calls, so an
  // early exit would leave the next call starting mid-string.
  const pattern = new RegExp(TOKEN_PATTERN.source, 'g');
  const tokens: Token[] = [];
  let cursor = 0;

  for (let match = pattern.exec(source); match; match = pattern.exec(source)) {
    if (match.index > cursor) {
      tokens.push({ kind: 'text', text: source.slice(cursor, match.index) });
    }

    const [whole, comment, tag, handlebars] = match;
    if (comment) {
      tokens.push({ kind: 'comment', text: whole });
    } else if (tag) {
      tokens.push({
        kind: 'tag',
        text: whole,
        name: elementName(tag),
        closing: tag.startsWith('</'),
      });
    } else if (handlebars) {
      // `{{#if}}` / `{{/if}}` / `{{else}}` are structure; `{{Request.Type}}` is content that sits
      // inside a sentence, so a break beside it would show up as a space.
      const inner = handlebars.slice(2, -2).trim();
      const isBlock = inner.startsWith('#') || inner.startsWith('/') || inner === 'else';
      tokens.push({ kind: isBlock ? 'block-directive' : 'expression', text: whole });
    }

    cursor = match.index + whole.length;
  }

  if (cursor < source.length) {
    tokens.push({ kind: 'text', text: source.slice(cursor) });
  }

  return tokens;
}

function elementName(tag: string): string {
  return /<\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/.exec(tag)?.[1]?.toLowerCase() ?? '';
}

function isBlockTag(token: Token | undefined): boolean {
  return token?.kind === 'tag' && BLOCK_TAGS.has(token.name ?? '');
}

/**
 * A token that may sit beside a break: a block-level tag, or a block directive. An inline tag
 * (`<a>`, `<span>`) and a `<td>` are excluded — a break beside the first renders as a space, and
 * keeping cells with their row is what holds the approval buttons on one line.
 */
function isBreakable(token: Token | undefined): boolean {
  return isBlockTag(token) || token?.kind === 'block-directive';
}

/**
 * Whether a break between these two tokens is guaranteed not to render.
 *
 * Both sides must be breakable, **and at least one must be a block-level tag**. The second
 * condition is the one an adversarial review added: two adjacent block directives are each
 * individually breakable, but the content between them may be inline, and breaking there turns
 * `XY` into `X Y` — or, between `{{#each}}` and an inner `{{#if}}`, indents every iteration.
 */
function breakBetween(before: Token | undefined, after: Token | undefined): boolean {
  if (!isBreakable(before) || !isBreakable(after)) return false;
  return isBlockTag(before) || isBlockTag(after);
}

/**
 * Formats `source` onto lines. Idempotent: the breaks it inserts are exactly the boundaries it
 * would choose again, so formatting an already-formatted body returns it unchanged.
 */
export function formatHandlebarsHtml(source: string): string {
  if (!source) return source;

  const tokens = tokenize(source);
  const out: string[] = [];
  let rawTextDepth = 0;

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i]!;

    if (token.kind === 'text') {
      // A whitespace-only run sitting exactly where a break belongs is insignificant, so it
      // collapses into the break inserted below. This condition must stay the mirror image of the
      // one that inserts the break, or formatting stops being idempotent. Any other text is
      // content and is kept byte for byte — including a single space separating two inline
      // elements, which is visible.
      if (
        rawTextDepth === 0 &&
        token.text.trim() === '' &&
        breakBetween(tokens[i - 1], tokens[i + 1])
      ) {
        continue;
      }
      out.push(token.text);
      continue;
    }

    if (rawTextDepth === 0 && breakBetween(lastEmittedToken(tokens, i), token)) {
      out.push('\n');
    }
    out.push(token.text);

    if (token.kind === 'tag' && RAW_TEXT_TAGS.has(token.name ?? '')) {
      rawTextDepth = Math.max(0, rawTextDepth + (token.closing ? -1 : 1));
    }
  }

  return out.join('');
}

/**
 * The token before index `i`, skipping a whitespace-only run that the loop above drops — so a
 * body that already carries breaks is judged on its tags rather than on its existing whitespace.
 */
function lastEmittedToken(tokens: Token[], i: number): Token | undefined {
  const previous = tokens[i - 1];
  if (previous?.kind === 'text' && previous.text.trim() === '') {
    return tokens[i - 2];
  }
  return previous;
}
